import { Capacitor } from '@capacitor/core';
import { useEffect } from 'react';

import { FIELD_SELECTOR, revealField } from '@/utils/keyboardInset';

const INSET_VAR = '--keyboard-inset';
const SCROLL_DELAY = 250;
const MIN_INSET = 80;

export const useKeyboardAvoidance = (enabled = true) => {
  useEffect(() => {
    if (!enabled) return;

    const viewport = window.visualViewport;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const isField = (node: Element | null): node is HTMLElement =>
      !!node && typeof (node as HTMLElement).matches === 'function' && node.matches(FIELD_SELECTOR);

    const keyboardHeight = () => {
      if (!viewport) return 0;
      const hidden = window.innerHeight - viewport.height - viewport.offsetTop;
      return hidden > MIN_INSET ? Math.round(hidden) : 0;
    };

    // On native, initKeyboardInset() owns the keyboard: it writes the inset on the plugin's
    // keyboardWillShow, before the keyboard animates in, and then reveals the focused field.
    // Writing the inset from here too would let the two sources overwrite each other, so it
    // is written here on the web only. The focus-in reveal below still runs on native, but
    // it measures the keyboard from visualViewport, which the native keyboard never shrinks.
    const ownsInset = !Capacitor.isNativePlatform();

    const setInset = (value: number) => {
      if (!ownsInset) return;
      const root = document.documentElement;
      root.style.setProperty(INSET_VAR, `${value}px`);
      if (value > 0) root.dataset.keyboard = 'open';
      else delete root.dataset.keyboard;
    };

    const reveal = (field: HTMLElement) =>
      revealField(field, window.innerHeight - keyboardHeight());

    const onFocusIn = (event: FocusEvent) => {
      const target = event.target as Element | null;
      if (!isField(target)) return;
      clearTimeout(timer);
      timer = setTimeout(() => reveal(target), SCROLL_DELAY);
    };

    // Only cancels a pending reveal. Resetting the inset here dropped the compensation
    // while the keyboard was still up — tapping a button inside an open modal moves
    // focus off the field without dismissing the keyboard. The viewport listener below
    // already clears the inset when the keyboard actually goes away.
    const onFocusOut = () => {
      clearTimeout(timer);
    };

    const onViewportChange = () => {
      setInset(keyboardHeight());
      const active = document.activeElement as Element | null;
      if (isField(active)) reveal(active);
    };

    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    viewport?.addEventListener('resize', onViewportChange);
    viewport?.addEventListener('scroll', onViewportChange);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      viewport?.removeEventListener('resize', onViewportChange);
      viewport?.removeEventListener('scroll', onViewportChange);
      setInset(0);
    };
  }, [enabled]);
};
